
import React, { useEffect, createContext, useContext, useState, useRef } from 'react';
import posthog from 'posthog-js';

type PostHogContextType = {
  isInitialized: boolean;
  trackEvent: (eventName: string, properties?: Record<string, any>) => void;
};

const PostHogContext = createContext<PostHogContextType>({
  isInitialized: false,
  trackEvent: () => {},
});

export const usePostHog = () => useContext(PostHogContext);

interface PostHogProviderProps {
  children: React.ReactNode;
}

export const PostHogProvider: React.FC<PostHogProviderProps> = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const initAttempted = useRef(false);
  
  useEffect(() => {
    // Skip if already attempted initialization
    if (initAttempted.current) return;
    initAttempted.current = true;
    
    // Only initialize in production or if explicitly enabled
    if (process.env.NODE_ENV !== 'production' && import.meta.env.VITE_ENABLE_ANALYTICS !== 'true') {
      return;
    }
    
    // Defer initialization until page is fully loaded
    const initPostHog = () => {
      try {
        if (typeof window === 'undefined' || isInitialized) return;
        
        const apiKey = import.meta.env.VITE_POSTHOG_API_KEY || 'phc_placeholder';
        const apiHost = import.meta.env.VITE_POSTHOG_HOST || 'https://app.posthog.com';
        
        posthog.init(apiKey, {
          api_host: apiHost,
          autocapture: false,         // Disable to reduce overhead
          capture_pageview: true,     // Keep pageview tracking
          capture_performance: false, // Disable for better performance
          advanced_disable_decide: true,
          loaded: (ph) => {
            // Opt out on localhost
            if (window.location.hostname === 'localhost') {
              ph.opt_out_capturing();
            }
            setIsInitialized(true);
          }
        });
      } catch (error) {
        console.error('Failed to initialize PostHog:', error);
      }
    };
    
    // Initialize after all critical content has loaded
    if (document.readyState === 'complete') {
      // Delay initialization to prioritize app rendering
      setTimeout(initPostHog, 2000);
    } else {
      window.addEventListener('load', () => {
        // Further delay on initial page load
        setTimeout(initPostHog, 3000);
      });
    }
  }, [isInitialized]);

  // Event tracking with rate limiting
  const trackEvent = (eventName: string, properties?: Record<string, any>) => {
    if (isInitialized && posthog) {
      try {
        posthog.capture(eventName, properties);
      } catch (error) {
        // Silent fail for analytics errors
      }
    }
  };
  
  return (
    <PostHogContext.Provider value={{ isInitialized, trackEvent }}>
      {children}
    </PostHogContext.Provider>
  );
};

export default PostHogProvider;
